import clsx from 'clsx';

import ForecastInfoItem, { InfoItemStyles } from '@/components/ForecastInfoItem';
import QuarterTemperature from '@/components/quartered-forecast/QuarterTemperature';
import { QuarteredForecastPart } from '@/models/QuarteredForecastPart';
import Barometer from '@/public/assets/forecast-info-icons/barometer.svg';
import Humidity from '@/public/assets/forecast-info-icons/humidity.svg';
import Precipitation from '@/public/assets/forecast-info-icons/precipitation.svg';
import WindArrow from '@/public/assets/forecast-info-icons/wind-arrow.svg';
import Wind from '@/public/assets/forecast-info-icons/wind.svg';

type QuarterDayInfoProps = {
  quarterInfo: QuarteredForecastPart;
  quarterName: string;
};

const QuarterDayInfo = ({ quarterInfo, quarterName }: QuarterDayInfoProps) => {
  const {
    temperature,
    feelTemperature,
    icon,
    description,
    precipitation,
    humidity,
    pressure,
    windSpeed,
    windDirection,
    windDegrees,
    isWindSpeedAbnormal,
    isPrecipitationAbnormal,
  } = quarterInfo;

  return (
    <div className="border-b border-dashed border-blue-grey-200 pb-2 pt-2.5 lg:mb-4 lg:border-b-0 lg:py-0">
      <div className="mb-2 text-sm font-medium text-blue-grey-500 lg:mb-2 lg:text-lg xl:hidden">{quarterName}</div>
      <div className="lg:flex lg:items-center">
        <div className="lg:mr-6 lg:flex lg:basis-[38%] lg:items-center xl:mr-10 xl:basis-[49%]">
          <div className="mr-5 hidden shrink-0 basis-[58px] text-lg font-medium text-blue-grey-500 xl:block xl:pb-0.5">
            {quarterName}
          </div>
          <QuarterTemperature {...{ icon, temperature, feelTemperature, description }} />
        </div>
        <div className="flex lg:basis-[59%] xl:basis-[47%] xl:pb-0.5">
          <div className="mr-10 md:mr-0 md:flex lg:basis-1/2">
            <ForecastInfoItem
              icon={<Precipitation className="h-[18px] w-[22px]" />}
              styleType={InfoItemStyles.DAYS}
              className="mb-2.5 md:mb-0 md:mr-10 lg:mr-0 lg:basis-1/2"
            >
              <span>
                <span className={clsx(isPrecipitationAbnormal && 'text-red')} data-testid="precipitation">
                  {precipitation.toFixed(1)}
                </span>
                &nbsp;мм
              </span>
            </ForecastInfoItem>
            <ForecastInfoItem
              icon={<Humidity className="h-[18px] w-[18px]" />}
              styleType={InfoItemStyles.DAYS}
              className="md:mr-10 lg:mr-0 lg:basis-1/2"
            >
              <span>{humidity}&nbsp;%</span>
            </ForecastInfoItem>
          </div>
          <div className="md:mr-0 md:flex lg:basis-1/2">
            <ForecastInfoItem
              icon={<Barometer className="h-[18px] w-[18px]" />}
              styleType={InfoItemStyles.DAYS}
              className="mb-2.5 md:mb-0 md:mr-10 lg:mr-0 lg:basis-1/2"
            >
              <span>{pressure}</span>
              <span className="lg:hidden">&nbsp;мм. рт. ст.</span>
            </ForecastInfoItem>
            <ForecastInfoItem
              icon={<Wind className="h-[18px] w-[22px]" />}
              subIcon={
                <WindArrow style={{ transform: `rotate(${windDegrees}deg)` }} className="w-[9px] text-blue-grey-500" />
              }
              styleType={InfoItemStyles.DAYS}
              className="lg:basis-1/2"
            >
              <span className="whitespace-nowrap lg:hidden">
                <span className={clsx(isWindSpeedAbnormal && 'text-red')} data-testid="wind-speed">
                  {windSpeed}
                </span>{' '}
                м/с, {windDirection}&nbsp;
              </span>
              <span
                className={clsx('mr-1.5 hidden self-baseline lg:inline', isWindSpeedAbnormal && 'text-red')}
                data-testid="wind-speed-desktop"
              >
                {windSpeed}
              </span>
              <span className="order-last ml-1 hidden self-baseline text-sm font-medium text-blue-grey-500 lg:inline">
                {windDirection}
              </span>
            </ForecastInfoItem>
          </div>
        </div>
      </div>
      <div className="mt-1 hidden text-sm text-grey-800 lg:block lg:pl-4.5 xl:hidden">{description}</div>
    </div>
  );
};

export default QuarterDayInfo;
